import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from '@tempo-io/sui-sonner';
import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';
import { WorkIntegration_Keys } from '@/common/api/types/workIntegrations';
import { useOrgName } from '@/common/hooks/useOrgName';
import { getLoopIdFromReturnUrl } from '@/common/utils/workIntegrations';
import { JiraIntegrationAPI } from '@/features/settings/work-integrations/api/jira';
import { OAuthApi } from '@/features/settings/work-integrations/api/oauth/OAuthApi';
import { WorkIntegrationsApi } from '@/features/settings/work-integrations/api/workIntegrations';
import {
  CREATE_INSTANCE_PARAM,
  CREATE_INSTANCE_VALUE,
} from '@/features/settings/work-integrations/constants';
import { workIntegrationsQueryKeys } from '@/features/settings/work-integrations/queryKeys';
import {
  mapAtlassianErrorParam,
  mapBackendError,
  type OAuthCallbackToastKey,
} from '../jira/-utils/oauthCallbackError';

export const providerFromSlug = (slug: string): WorkIntegration_Keys | undefined => {
  const key = slug.toUpperCase();
  return (Object.values(WorkIntegration_Keys) as string[]).includes(key)
    ? (key as WorkIntegration_Keys)
    : undefined;
};

export const oauthCallbackSchema = z.object({
  state: z.string(),
  code: z.string().optional(),
  error: z.string().optional(),
  integrationId: z.coerce.number().optional(),
  instanceId: z.string().optional(),
  projectId: z.string().optional(),
});

const mapProviderErrorParam = (
  provider: WorkIntegration_Keys,
  error: string,
): OAuthCallbackToastKey | undefined =>
  provider === 'JIRA' ? (mapAtlassianErrorParam(error) ?? undefined) : undefined;

const getCallbackSearchParams = (
  provider: WorkIntegration_Keys,
  isReauth: boolean,
  isCreateInstance: boolean,
  oauthCredentialsId: number,
  loopId?: number,
  callbackSource?: string,
) => {
  if (isReauth) return {};
  if (isCreateInstance) {
    return {
      [CREATE_INSTANCE_PARAM]: CREATE_INSTANCE_VALUE,
      integration: provider,
      oauthCredentialsId,
      ...(loopId !== undefined ? { loopId } : {}),
      ...(callbackSource !== undefined ? { callbackSource } : {}),
    };
  }
  return {
    integration: provider,
    oauthCredentialsId,
  };
};

export function useOAuthCallback(
  provider: WorkIntegration_Keys,
  { state, code, error }: { state: string; code?: string; error?: string },
) {
  const { t } = useTranslation();
  const { isLoading } = useAuth0();
  const navigate = useNavigate();
  const orgName = useOrgName();
  const queryClient = useQueryClient();

  useEffect(() => {
    const navigateToIntegrations = () =>
      navigate({ to: '/org/$org/settings/work-integrations', params: { org: orgName } });

    const showErrorToastByKey = (key: OAuthCallbackToastKey) => {
      toast.error(() => (
        <Trans
          i18nKey={`common.settings.work-integrations.toasts.oauthSettings.${key}`}
          t={t}
          components={{
            strong: <strong className="font-semibold" />,
            break: <br />,
          }}
        />
      ));
    };

    const failWithToast = (key: OAuthCallbackToastKey) => {
      navigateToIntegrations();
      showErrorToastByKey(key);
    };

    const handleCallback = async () => {
      if (isLoading) {
        return;
      }

      if (error) {
        if (error === 'access_denied') {
          navigateToIntegrations();
          toast.error(() => (
            <Trans
              i18nKey="common.settings.work-integrations.toasts.oauthSettings.error"
              t={t}
              components={{
                strong: <strong className="font-semibold" />,
                break: <br />,
              }}
            />
          ));
          return;
        }
        failWithToast(mapProviderErrorParam(provider, error) ?? 'unexpectedFailure');
        return;
      }

      const codeParam = code !== undefined ? `&code=${code}` : '';
      const returnUrl = `/integrations/${provider.toLowerCase()}/callback?state=${state}${codeParam}`;
      let response: Awaited<ReturnType<typeof OAuthApi.response>>;
      try {
        response = await OAuthApi.response(provider, { returnUrl });
      } catch (err) {
        const toastKey = await mapBackendError(err);
        failWithToast(toastKey);
        return;
      }

      const isReauth = response.returnUrl?.includes('reauth=true') ?? false;

      let reauthIntegrationId: number | undefined;
      let reauthInstanceId: string | undefined;
      let reauthProjectId: string | undefined;

      if (isReauth && response.returnUrl) {
        const url = new URL(response.returnUrl, window.location.origin);
        const integrationIdParam = url.searchParams.get('integrationId');
        reauthIntegrationId = integrationIdParam ? Number(integrationIdParam) : undefined;
        reauthInstanceId = url.searchParams.get('instanceId') || undefined;
        reauthProjectId = url.searchParams.get('projectId') || undefined;
      }

      if (
        isReauth &&
        provider === WorkIntegration_Keys.JIRA &&
        reauthIntegrationId &&
        reauthInstanceId &&
        reauthProjectId
      ) {
        try {
          const integrations = await WorkIntegrationsApi.getAllWorkIntegrations({
            filter: { ids: [reauthIntegrationId] },
          });
          const integration = integrations[0];

          if (!integration) {
            navigateToIntegrations();
            toast.error(
              t(
                ($) =>
                  $.common.settings['work-integrations'].toasts.oauthSettings.integrationNotFound,
              ),
            );
            return;
          }

          const credentialsChanged = response.oauthCredentialsId !== integration.credentialsId;

          const instances = await JiraIntegrationAPI.getInstances();
          const currentInstance = instances.find((inst) => inst.id === reauthInstanceId);

          if (!currentInstance) {
            navigateToIntegrations();
            toast.error(() => (
              <Trans
                i18nKey="common.settings.work-integrations.toasts.oauthSettings.validationError"
                t={t}
                components={{
                  strong: <strong className="font-semibold" />,
                  break: <br />,
                }}
              />
            ));
            return;
          }

          await WorkIntegrationsApi.updateWorkIntegrationCredentials({
            workIntegrationId: reauthIntegrationId,
            oauthCredentialsId: response.oauthCredentialsId,
          });

          await queryClient.invalidateQueries({
            queryKey: workIntegrationsQueryKeys.oauthCredentialStatuses.all(),
          });

          if (credentialsChanged) {
            toast.warning(() => (
              <Trans
                i18nKey="common.settings.work-integrations.toasts.oauthSettings.differentUserReauth"
                t={t}
                components={{
                  strong: <strong className="font-semibold" />,
                  break: <br />,
                }}
              />
            ));
          } else {
            toast.success(() => (
              <Trans
                i18nKey="common.settings.work-integrations.toasts.oauthSettings.tokenRefreshed"
                t={t}
                components={{
                  strong: <strong className="font-semibold" />,
                  break: <br />,
                }}
              />
            ));
          }
        } catch {
          navigateToIntegrations();
          toast.error(() => (
            <div>
              <Trans
                i18nKey="common.settings.work-integrations.toasts.oauthSettings.validationError"
                t={t}
                components={{
                  strong: <strong className="font-semibold" />,
                  break: <br />,
                }}
              />
            </div>
          ));
          return;
        }
      } else if (isReauth && reauthInstanceId) {
        await queryClient.invalidateQueries({
          queryKey: workIntegrationsQueryKeys.integrationInstances.all(),
        });
        await queryClient.invalidateQueries({
          queryKey: workIntegrationsQueryKeys.oauthCredentialStatuses.all(),
        });
        toast.success(() => (
          <Trans
            i18nKey="common.settings.work-integrations.toasts.oauthSettings.tokenRefreshed"
            t={t}
            components={{
              strong: <strong className="font-semibold" />,
              break: <br />,
            }}
          />
        ));
      } else if (isReauth) {
        navigateToIntegrations();
        toast.error(() => (
          <Trans
            i18nKey="common.settings.work-integrations.toasts.oauthSettings.validationError"
            t={t}
            components={{
              strong: <strong className="font-semibold" />,
              break: <br />,
            }}
          />
        ));
        return;
      } else {
        toast.success(() => (
          <Trans
            i18nKey="common.settings.work-integrations.toasts.oauthSettings.success"
            t={t}
            components={{
              strong: <strong className="font-semibold" />,
              break: <br />,
            }}
          />
        ));
      }

      if (response.returnUrl?.includes('/settings/work-integrations')) {
        const isCreateInstance =
          response.returnUrl?.includes(`${CREATE_INSTANCE_PARAM}=${CREATE_INSTANCE_VALUE}`) ??
          false;

        const callbackLoopId =
          isCreateInstance && response.returnUrl
            ? getLoopIdFromReturnUrl(response.returnUrl)
            : undefined;

        const callbackSource =
          isCreateInstance && response.returnUrl
            ? (new URL(response.returnUrl, window.location.origin).searchParams.get(
                'callbackSource',
              ) ?? undefined)
            : undefined;

        const reauthReturnUrl = response.returnUrl
          ? new URL(response.returnUrl, window.location.origin)
          : null;
        const detailPageMatch = reauthReturnUrl?.pathname.match(
          /\/org\/([^/]+)\/settings\/work-integrations\/(\d+)/,
        );
        const isDetailPageReauth = isReauth && !!detailPageMatch;

        if (isDetailPageReauth && detailPageMatch?.[1] && detailPageMatch?.[2]) {
          navigate({
            to: '/org/$org/settings/work-integrations/$integrationId',
            params: { org: detailPageMatch[1], integrationId: detailPageMatch[2] },
            search: { reauthed: true },
          });
        } else {
          navigate({
            to: '/org/$org/settings/work-integrations',
            params: { org: orgName },
            search: getCallbackSearchParams(
              provider,
              isReauth,
              isCreateInstance,
              response.oauthCredentialsId,
              callbackLoopId,
              callbackSource,
            ),
          });
        }
      } else if (response.returnUrl?.includes('/loop/')) {
        const returnUrlObj = new URL(response.returnUrl, window.location.origin);
        const isCreateInstance = response.returnUrl.includes(
          `${CREATE_INSTANCE_PARAM}=${CREATE_INSTANCE_VALUE}`,
        );
        const callbackLoopId = isCreateInstance
          ? getLoopIdFromReturnUrl(response.returnUrl)
          : undefined;
        const callbackSource = isCreateInstance
          ? (returnUrlObj.searchParams.get('callbackSource') ?? undefined)
          : undefined;

        navigate({
          to: returnUrlObj.pathname,
          search: isCreateInstance
            ? {
                [CREATE_INSTANCE_PARAM]: CREATE_INSTANCE_VALUE,
                oauthCredentialsId: response.oauthCredentialsId,
                ...(callbackLoopId !== undefined ? { loopId: callbackLoopId } : {}),
                ...(callbackSource !== undefined ? { callbackSource } : {}),
              }
            : {},
          replace: true,
        } as Parameters<typeof navigate>[0]);
      } else if (response.returnUrl?.includes('/teams')) {
        navigate({
          to: '/org/$org/settings/teams',
          params: { org: orgName },
          search: {
            dialog: 'import',
          },
        });
      }
    };

    handleCallback().catch(async (err: unknown) => {
      const toastKey = await mapBackendError(err);
      failWithToast(toastKey);
    });
  }, [provider, isLoading, state, code, navigate, error, orgName, t, queryClient]);
}
